"use client";

// Step 8.5 Half A0 commit 0.2 / AC406, AC407, AC408: 3-step signup
// wizard replacing the flat single-page form from Step 8c.2 commit 16.
//
// State management:
//   - React Hook Form holds the form values across all 3 steps.
//   - currentStep persisted in `?step=` query param so the back button
//     replays the previous step with field values intact.
//   - sessionId (UUID) generated once on mount, used to scope S3
//     upload object keys.
//
// Submission:
//   - Step 1 + Step 2 are pure client-side navigation.
//   - Step 3 submits to POST /api/signup with the full payload.
//     Personal-event branch posts event fields; business default
//     branch posts S3 object keys for any uploaded docs.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { FormProvider, useForm, type SubmitErrorHandler } from "react-hook-form";
import type { FulfillmentChoice } from "@/lib/delivery/fulfillment";
import type { ShippingMethod } from "@/lib/delivery/out-of-state-pricing";
import { SignupStep1 } from "./SignupStep1";
import { SignupStep2 } from "./SignupStep2";
import { SignupStep3Business } from "./SignupStep3Business";
import { SignupStep3Personal } from "./SignupStep3Personal";
import { trackEvent } from "@/lib/analytics/ga";

export type SignupFormValues = {
  // Step 1
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  password: string;
  // D-037 c5.3: bilingual email pipeline. Drives the EN/ES variant of
  // the signup-welcome, approval, rejection, magic-link, and first-
  // sign-in templates.
  preferredLanguage: "en" | "es";
  // Step 2
  businessType: string;
  yearsInBusiness: string;
  // #346: businessAddress is now the STREET line; city/state/zip are separate
  // required fields so an incomplete address can't be submitted and out-of-state
  // detection keys off `state` (not a ZIP scraped from free text).
  businessAddress: string;
  city: string;
  state: string; // 2-letter US code; "" = not yet selected
  zip: string;
  // True only when the user clicked a Google Places suggestion. Stays
  // false on raw-typed addresses so admin sees an "unverified" flag at
  // approval time and verifies manually. Prevents a Places API outage
  // from blocking signups.
  addressVerified: boolean;
  website: string;
  monthlyBudget: string;
  howHeard: string;
  // PASTE #235: out-of-state fulfillment (shown on Step 2 only when the
  // business ZIP is non-Florida). choice "" = not yet answered.
  fulfillment: {
    choice: "" | FulfillmentChoice;
    truckerName: string;
    truckerContact: string;
    carrierName: string;
    carrierAccount: string;
    sendLabels: boolean;
    note: string;
  };
  // #432: out-of-state shipping method chosen up front (out-of-FL only). AIRPORT
  // is the approved default; shippingAirportConfirmed is a transient UI gate (the
  // client must confirm the suggested airport) and is not persisted.
  shippingMethod: "" | ShippingMethod;
  shippingAirport: { iata: string; name: string } | null;
  shippingAirportConfirmed: boolean;
  // Step 3 - Personal event branch
  eventType: string;
  eventDate: string;
  headcountOrBudget: string;
  // Step 3 - Business default branch
  businessLicenseObjectKey: string;
  resaleCertObjectKey: string;
  // Always
  agreedToTerms: boolean;
};

const DEFAULT_VALUES: SignupFormValues = {
  businessName: "",
  contactName: "",
  email: "",
  phone: "",
  password: "",
  preferredLanguage: "en",
  businessType: "",
  yearsInBusiness: "",
  businessAddress: "",
  city: "",
  state: "",
  zip: "",
  addressVerified: false,
  website: "",
  monthlyBudget: "",
  howHeard: "",
  fulfillment: {
    choice: "",
    truckerName: "",
    truckerContact: "",
    carrierName: "",
    carrierAccount: "",
    sendLabels: false,
    note: "",
  },
  // #432: AIRPORT is the approved default for a new out-of-FL client. The
  // suggested airport starts UNCONFIRMED - the client must confirm to continue.
  shippingMethod: "AIRPORT",
  shippingAirport: null,
  shippingAirportConfirmed: false,
  eventType: "",
  eventDate: "",
  headcountOrBudget: "",
  businessLicenseObjectKey: "",
  resaleCertObjectKey: "",
  agreedToTerms: false,
};

type StepNum = 1 | 2 | 3;

// URGENT signup fix: which step owns each field. A failed whole-form submit
// (handleSubmit's validation) must jump the user to the step that holds the
// first invalid field instead of dying silently on step 3 - the revenue-
// blocking "Submit does nothing, no error" bug. Nested groups (fulfillment.*)
// key off their top-level name.
const STEP_OF_FIELD: Record<string, StepNum> = {
  businessName: 1,
  contactName: 1,
  email: 1,
  phone: 1,
  password: 1,
  preferredLanguage: 1,
  businessType: 2,
  yearsInBusiness: 2,
  businessAddress: 2,
  city: 2,
  state: 2,
  zip: 2,
  monthlyBudget: 2,
  website: 2,
  howHeard: 2,
  fulfillment: 2,
  fulfillmentChoice: 2,
  addressVerified: 2,
  eventType: 3,
  eventDate: 3,
  headcountOrBudget: 3,
  agreedToTerms: 3,
  businessLicenseObjectKey: 3,
  resaleCertObjectKey: 3,
};

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback: not cryptographically strong but only used for upload
  // namespacing, never for security.
  return "00000000-0000-4000-8000-000000000000".replace(/[018]/g, (c) => {
    const n = Number(c);
    const r = (Math.random() * 16) | 0;
    return (n ^ (r & (15 >> (n / 4)))).toString(16);
  });
}

export function SignupWizard() {
  const router = useRouter();
  const params = useSearchParams();
  const stepFromUrl = Number(params.get("step") ?? "1") as StepNum;
  const [currentStep, setCurrentStep] = useState<StepNum>(
    stepFromUrl === 2 || stepFromUrl === 3 ? stepFromUrl : 1,
  );
  const [sessionId] = useState(() => uuid());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const methods = useForm<SignupFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: "onChange",
  });

  useEffect(() => {
    const sp = new URLSearchParams(params.toString());
    sp.set("step", String(currentStep));
    router.replace(`/sign-up?${sp.toString()}`, { scroll: false });
  }, [currentStep, params, router]);

  const goToStep = useCallback((step: StepNum) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  const businessType = methods.watch("businessType");
  const isPersonalEvent = businessType === "personal-event";

  const handleSubmitFinal = useCallback(
    async (values: SignupFormValues) => {
      setSubmitting(true);
      setSubmitError(null);
      try {
        const payload: Record<string, unknown> = {
          businessName: values.businessName,
          contactName: values.contactName,
          email: values.email,
          phone: values.phone,
          password: values.password,
          preferredLanguage: values.preferredLanguage,
          businessAddress: values.businessAddress,
          // URGENT signup fix - THE root cause: #346 added city/state/zip to the
          // form + the server validator + the DB row, but NEVER added them to
          // this POST payload. So every submit sent no city/state/zip -> the
          // server 400'd "City is required / Select your state / Enter a 5-digit
          // ZIP" -> the wizard bounced back showing those errors on the FILLED
          // fields. Include them so the server gets what the user actually typed.
          city: values.city,
          state: values.state,
          zip: values.zip,
          addressVerified: values.addressVerified === true,
          businessType: values.businessType,
          yearsInBusiness: values.yearsInBusiness || undefined,
          website: values.website || undefined,
          monthlyBudget: values.monthlyBudget || undefined,
          howHeard: values.howHeard || undefined,
          sessionId,
          agreedToTerms: values.agreedToTerms,
        };
        // PASTE #235: include the out-of-state fulfillment arrangement
        // only when the client actually answered it (non-FL). The server
        // re-validates + normalizes; FL clients send nothing here.
        if (values.fulfillment?.choice) {
          const f = values.fulfillment;
          payload.fulfillment = {
            choice: f.choice,
            truckerName: f.truckerName || undefined,
            truckerContact: f.truckerContact || undefined,
            carrierName: f.carrierName || undefined,
            carrierAccount: f.carrierAccount || undefined,
            sendLabels: f.sendLabels === true,
            note: f.note || undefined,
          };
        }
        // #432: the out-of-state shipping method + confirmed airport. The server
        // only persists these when the address is out-of-FL.
        if (values.shippingMethod) {
          payload.shippingMethod = values.shippingMethod;
          if (values.shippingMethod === "AIRPORT" && values.shippingAirport) {
            payload.shippingAirport = values.shippingAirport;
          }
        }
        if (isPersonalEvent) {
          payload.eventType = values.eventType;
          payload.eventDate = values.eventDate;
          payload.headcountOrBudget = values.headcountOrBudget;
        } else {
          if (values.businessLicenseObjectKey) {
            payload.businessLicenseObjectKey = values.businessLicenseObjectKey;
          }
          if (values.resaleCertObjectKey) {
            payload.resaleCertObjectKey = values.resaleCertObjectKey;
          }
        }
        const r = await fetch("/api/signup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (r.status === 429) {
          setSubmitError("Too many attempts. Try again in about an hour.");
          return;
        }
        if (r.status === 409) {
          setSubmitError(
            "Email already registered. Sign in or use a different email.",
          );
          return;
        }
        if (r.status === 400) {
          const body = (await r.json().catch(() => ({}))) as {
            fields?: Record<string, string>;
          };
          if (body.fields) {
            Object.entries(body.fields).forEach(([k, msg]) =>
              methods.setError(k as keyof SignupFormValues, { message: msg }),
            );
            // Take the user back to the step that owns the first field
            // with an error.
            const firstField = Object.keys(body.fields)[0];
            const step1Fields = new Set([
              "businessName",
              "contactName",
              "email",
              "phone",
              "password",
            ]);
            // URGENT signup fix (trap #1): the #346 fields (city/state/zip)
            // and out-of-state fulfillment were MISSING here, so a server 400
            // on any of them stranded the user on step 3 with no navigation
            // and no error. Include every step-2 field the server can reject.
            const step2Fields = new Set([
              "businessType",
              "businessAddress",
              "monthlyBudget",
              "yearsInBusiness",
              "city",
              "state",
              "zip",
              "fulfillment",
              "fulfillmentChoice",
            ]);
            if (step1Fields.has(firstField)) goToStep(1);
            else if (step2Fields.has(firstField)) goToStep(2);
            else setSubmitError("Please fix the highlighted fields and try again.");
            return;
          }
          setSubmitError("Please fix the highlighted fields and try again.");
          return;
        }
        if (!r.ok) {
          setSubmitError(`Sign-up failed (HTTP ${r.status}).`);
          return;
        }
        setSubmitSuccess(true);
        // PASTE #276: GA4 conversion - wholesale application submitted.
        // No PII in the payload.
        trackEvent("signup_application_submitted");
        const email = encodeURIComponent(values.email);
        router.push(`/sign-up/success?email=${email}`);
      } catch (err) {
        setSubmitError(
          err instanceof Error ? err.message : "Network error",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [isPersonalEvent, methods, router, sessionId, goToStep],
  );

  // URGENT signup fix: the never-silent guard. RHF calls this when the final
  // handleSubmit's whole-form validation FAILS - previously there was no
  // onInvalid handler, so a required field on step 1/2 (notably the years/
  // budget controls and the #346 city/state/zip) made "Submit" do NOTHING:
  // no POST, no error, button still enabled, stranded on step 3. Now we jump
  // to the step that owns the first invalid field and surface a visible error.
  const handleInvalid = useCallback<SubmitErrorHandler<SignupFormValues>>(
    (formErrors) => {
      const firstField = Object.keys(formErrors)[0];
      const step = (firstField && STEP_OF_FIELD[firstField]) || currentStep;
      setSubmitError(
        "Some required details still need your attention - we've taken you to the field to fix. Please complete the highlighted field(s), then submit again.",
      );
      if (step !== currentStep) goToStep(step);
    },
    [currentStep, goToStep],
  );

  const ProgressBar = useMemo(
    () => (
      <div
        data-testid="signup-progress"
        style={{ display: "flex", gap: "8px", marginBottom: "32px" }}
      >
        {[1, 2, 3].map((n) => (
          <div
            key={n}
            style={{
              flex: 1,
              height: "4px",
              borderRadius: "2px",
              backgroundColor:
                n <= currentStep ? "#77C1BB" : "#e6e6e6",
            }}
          />
        ))}
      </div>
    ),
    [currentStep],
  );

  if (submitSuccess) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <section
        data-testid="signup-wizard"
        data-step={currentStep}
        className="mx-auto w-full max-w-lg px-4 py-12 md:px-6 md:py-16"
      >
        <div className="mx-auto w-full max-w-md rounded-xl bg-white p-10 shadow-lg">
          <div className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-[#4a8b85]">
            Step {currentStep} of 3 &middot;{" "}
            {currentStep === 3 ? "About 30 seconds" : "About 1 minute"}
          </div>
          {ProgressBar}

          {currentStep === 1 && (
            <SignupStep1
              submitError={submitError}
              onContinue={() => goToStep(2)}
            />
          )}
          {currentStep === 2 && (
            <SignupStep2
              submitError={submitError}
              onBack={() => goToStep(1)}
              onContinue={() => goToStep(3)}
            />
          )}
          {currentStep === 3 && (
            isPersonalEvent ? (
              <SignupStep3Personal
                submitting={submitting}
                submitError={submitError}
                onBack={() => goToStep(2)}
                onSubmit={handleSubmitFinal}
                onInvalid={handleInvalid}
              />
            ) : (
              <SignupStep3Business
                submitting={submitting}
                submitError={submitError}
                sessionId={sessionId}
                onBack={() => goToStep(2)}
                onSubmit={handleSubmitFinal}
                onInvalid={handleInvalid}
              />
            )
          )}
        </div>
        <p className="mt-8 text-center text-xs text-zinc-500">
          Magnaflor Farm Fresh Flowers &middot; sales@magnaflor.com
        </p>
      </section>
    </FormProvider>
  );
}
